/**
 * Накладная: фото по центру, справа — что с ней делать.
 *
 * Три случая, как в боте (§5.1 п. 4):
 * - сопоставлена с документом StoreHouse — что с ней и что сделать, строки
 *   бумаги и документа парами, кнопки из `actions`;
 * - есть черновик по фото — редактор строк;
 * - ждёт черновика — что происходит и «Подготовить черновик по фото».
 *
 * Какие кнопки показывать, решает сервер (`actions`): те же правила, что у
 * бота, и портал их не угадывает. Слова пояснения тоже его (`guide`,
 * `origin`): портал и бот говорят одно.
 *
 * Сопоставленная накладная читается сверху вниз как ответ на три вопроса
 * владельца (30.09.2026): что случилось и кто завёл документ, в чём именно
 * разница, что нажать и что после этого будет. До того экран показывал
 * «почему такой цвет», две таблицы без связи и кнопки «Верно» и «Ошибка»
 * с объяснением только во всплывающей подсказке.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { portalApi, qty, rub } from '../../api';
import type {
  ComparedLine,
  Message,
  Proposal,
  ReceiptAction,
  ReceiptDetail,
  ReceiptQuestion,
  Supplier,
} from '../../api';
import { Icon } from '../../icons';
import { Notice, Pill, isoDate, tone, when } from '../../ui';
import { PhotoViewer } from './PhotoViewer';
import { ProposalEditor, useDebounced } from './ProposalEditor';
import { SECTION_TITLES } from './Queue';

export function receiptKey(id: number) {
  return ['receipts', 'detail', id] as const;
}

type Mark = Exclude<ReceiptAction, 'prepare_draft'>;

/**
 * Кнопка-ответ: что нажимаю и что после этого будет. Последствие написано
 * на самой кнопке, а не во всплывающей подсказке — её на телефоне нет, а на
 * компьютере до неё надо догадаться.
 */
const MARKS: Record<Mark, { title: string; hint: string }> = {
  verify: {
    title: 'Проверил — можно проводить',
    hint: 'Сверил с бумагой, черновик верный. Накладная перейдёт в «Можно проводить».',
  },
  posted: {
    title: 'Провёл в StoreHouse',
    hint: 'Накладная уйдёт из списка; агент подтвердит проведение при следующем чтении.',
  },
  posted_ok: {
    title: 'Документ верный',
    hint: 'В StoreHouse всё правильно, расхождение объяснимо. Накладная уйдёт из списка.',
  },
  posted_error: {
    title: 'В документе ошибка',
    hint: 'Накладная останется в списке, пока документ не исправят в StoreHouse.',
  },
};

function statusText(receipt: ReceiptDetail): string {
  if (receipt.section) return SECTION_TITLES[receipt.section];
  if (receipt.draft?.posted) return 'Проведена';
  return 'Накладная';
}

export function Detail({ id }: { id: number }) {
  const query = useQuery<ReceiptDetail, ApiError>({
    queryKey: receiptKey(id),
    queryFn: () => portalApi.receipt(id),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  if (query.isPending) return <Loading />;
  if (query.error) {
    return query.error.status === 404 ? (
      <ErrorState
        title="Накладной нет"
        text="Её могли уже провести или удалить. Вернитесь к очереди."
      />
    ) : (
      <ErrorState error={query.error} onRetry={() => query.refetch()} />
    );
  }

  const receipt = query.data;
  return (
    <div className="detail">
      <PhotoViewer key={receipt.id} receiptId={receipt.id} pages={receipt.pages} />
      <DetailPanel receipt={receipt} />
    </div>
  );
}

function DetailPanel({ receipt }: { receipt: ReceiptDetail }) {
  const supplier = receipt.supplier?.name ?? receipt.new_supplier_name;
  const proposalEditable = receipt.proposal && receipt.proposal.status === 'prepared';

  return (
    <aside className="panel" aria-label="Накладная">
      <header className="panel__head">
        <div className="panel__title-row">
          <h2 className="panel__title">
            {supplier ?? 'Поставщик не узнан'}
            {!receipt.supplier && receipt.new_supplier_name && (
              <span className="panel__new"> · новый поставщик</span>
            )}
          </h2>
          <Pill colour={receipt.colour}>{statusText(receipt)}</Pill>
        </div>
        <p className="panel__meta">
          {[
            receipt.draft?.label ??
              (receipt.paper?.number ? `№ ${receipt.paper.number}` : null),
            !receipt.draft && receipt.paper?.date ? `от ${isoDate(receipt.paper.date)}` : null,
            receipt.received_at ? `сдана ${when(receipt.received_at)}` : null,
            receipt.receiver,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </header>

      {receipt.note && (
        <div className="agent-note">
          <span className="agent-note__icon">
            <Icon name="sparkle" size={14} />
          </span>
          <p>{receipt.note}</p>
        </div>
      )}

      {receipt.questions.length > 0 && (
        <Questions receiptId={receipt.id} questions={receipt.questions} />
      )}

      {proposalEditable && receipt.proposal ? (
        <ProposalEditor receiptId={receipt.id} proposal={receipt.proposal} />
      ) : receipt.proposal ? (
        <ProposalStatus proposal={receipt.proposal} />
      ) : receipt.draft ? (
        <LinkedDraft receipt={receipt} />
      ) : !receipt.supplier && receipt.new_supplier_name ? (
        <NewSupplier receipt={receipt} />
      ) : (
        <Waiting receipt={receipt} />
      )}

      <Actions receipt={receipt} />
    </aside>
  );
}

/* ── Вопросы агента ──────────────────────────────────────────────────── */

function refreshReceipts(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['receipts'] });
  void queryClient.invalidateQueries({ queryKey: ['today'] });
}

function Questions({ receiptId, questions }: { receiptId: number; questions: ReceiptQuestion[] }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const answer = useMutation<Message, ApiError, { questionId: number; code: string }>({
    mutationFn: ({ questionId, code }) => portalApi.answerQuestion(receiptId, questionId, code),
    onSuccess: (data) => {
      setResult({ ok: true, text: data.message });
      refreshReceipts(queryClient);
    },
    onError: (error) =>
      setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' }),
  });
  return (
    <div className="panel__block">
      <h3 className="block-title">Вопросы агента</h3>
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      {questions.map((question) => (
        <div key={question.id} className="question">
          <p className="question__text">{question.text}</p>
          <div className="row">
            {question.answers.map((choice) => (
              <button
                key={choice.code}
                type="button"
                className="button button--small"
                disabled={answer.isPending}
                onClick={() => answer.mutate({ questionId: question.id, code: choice.code })}
              >
                {choice.title}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Новый поставщик ─────────────────────────────────────────────────── */

function NewSupplier({ receipt }: { receipt: ReceiptDetail }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const q = useDebounced(text.trim());
  const found = useQuery<Supplier[], ApiError>({
    queryKey: ['suppliers', q],
    queryFn: () => portalApi.suppliers(q),
    enabled: q.length >= 2,
    staleTime: 60_000,
  });
  const done = (data: Message) => {
    setResult({ ok: true, text: data.message });
    void queryClient.invalidateQueries({ queryKey: receiptKey(receipt.id) });
    refreshReceipts(queryClient);
  };
  const failed = (error: ApiError) =>
    setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' });
  const choose = useMutation<Message, ApiError, number>({
    mutationFn: (supplierId) => portalApi.setSupplier(receipt.id, supplierId),
    onSuccess: done,
    onError: failed,
  });
  const recheck = useMutation<Message, ApiError>({
    mutationFn: () => portalApi.recheckSupplier(receipt.id),
    onSuccess: done,
    onError: failed,
  });
  const busy = choose.isPending || recheck.isPending;

  return (
    <div className="panel__block">
      <h3 className="block-title">Поставщика нет в StoreHouse</h3>
      <p className="muted">
        Накладную сдали как нового поставщика «{receipt.new_supplier_name}», и агент не нашёл его
        карточку. Если она есть под другим названием — выберите её ниже. Если нет — заведите в
        StoreHouse и нажмите «Проверить снова».
      </p>
      {receipt.paper_inn && (
        <p>
          ИНН с фото: <b>{receipt.paper_inn}</b>
        </p>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      <div className="picker">
        <div className="picker__search">
          <label className="search search--small">
            <Icon name="search" size={14} />
            <input
              className="search__input"
              type="search"
              placeholder="Найти поставщика в StoreHouse"
              value={text}
              onChange={(event) => setText(event.target.value)}
              aria-label="Найти поставщика в StoreHouse"
            />
          </label>
        </div>
        {q.length >= 2 && (
          <ul className="picker__results">
            {found.isPending ? (
              <li className="faint">Ищу…</li>
            ) : (found.data ?? []).length === 0 ? (
              <li className="faint">Не нашлось — попробуйте часть названия.</li>
            ) : (
              found.data!.map((supplier) => (
                <li key={supplier.id}>
                  <button
                    type="button"
                    className="picker__result"
                    disabled={busy}
                    onClick={() => choose.mutate(supplier.id)}
                  >
                    <span>{supplier.name}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
      <div className="row">
        <button type="button" className="button" disabled={busy} onClick={() => recheck.mutate()}>
          {recheck.isPending ? 'Проверяю…' : 'Проверить снова'}
        </button>
      </div>
    </div>
  );
}

/* ── Сопоставленный черновик ─────────────────────────────────────────── */

export function LinkedDraft({ receipt }: { receipt: ReceiptDetail }) {
  const draft = receipt.draft;
  const document = draft?.posted ? 'Документ в StoreHouse' : 'Черновик в StoreHouse';
  return (
    <>
      {receipt.guide ? (
        <section className={`guide guide--${tone(receipt.colour)}`} aria-label="Что сделать">
          <h3 className="guide__title">{receipt.guide.title}</h3>
          {receipt.guide.text && <p className="guide__text">{receipt.guide.text}</p>}
          <ol className="guide__steps">
            {receipt.guide.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </section>
      ) : (
        receipt.origin && <p className="muted">{receipt.origin.text}</p>
      )}

      {(receipt.reasons.length > 0 || receipt.difference?.hint) && (
        <div className="panel__block">
          <h3 className="block-title">Что не сходится</h3>
          {receipt.reasons.length > 0 && (
            <ul className="reasons">
              {receipt.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
          {receipt.difference?.hint && (
            <div className="agent-note">
              <span className="agent-note__icon">
                <Icon name="sparkle" size={14} />
              </span>
              <p>
                Разница {rub(receipt.difference.amount)}. {receipt.difference.hint}
              </p>
            </div>
          )}
        </div>
      )}
      {receipt.verified && <Notice>Вы отметили: проверено.</Notice>}

      {receipt.comparison ? (
        <Comparison receipt={receipt} lines={receipt.comparison} document={document} />
      ) : (
        <SeparateTables receipt={receipt} document={document} />
      )}

      {receipt.skipped.length > 0 && (
        <div className="panel__block">
          <h3 className="block-title">Агент не проверил сам</h3>
          <p className="muted">
            {receipt.skipped.join(', ')} — сверьте с фото глазами.
            {receipt.draft_lines_pending &&
              ' Строки, товары и единицы агент проверит сам, когда прочитает документ.'}
          </p>
        </div>
      )}
    </>
  );
}

/** Строки бумаги и документа парами: сразу видно, какой строки нет и где. */
function Comparison({
  receipt,
  lines,
  document,
}: {
  receipt: ReceiptDetail;
  lines: ComparedLine[];
  document: string;
}) {
  // Суммы приходят только тому, у кого флаг денег (D47): нет — их и не видно.
  const money = lines.some((line) => (line.paper?.amount ?? line.draft?.net ?? null) !== null);
  const paperTotal = rub(receipt.paper?.total);
  const draftTotal = rub(receipt.draft?.total);
  const sub = (quantity: string | null, unit: string | null, amount: string | null) =>
    [`${qty(quantity)}${unit ? ` ${unit}` : ''}`, money ? rub(amount) : null]
      .filter(Boolean)
      .join(' · ');
  return (
    <div className="panel__block">
      <h3 className="block-title">Сравнение строк</h3>
      <div className="table-wrap">
        <table className="compare">
          <thead>
            <tr>
              <th>На бумаге</th>
              <th>{document}</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index} className={`compare__row compare__row--${line.state}`}>
                <td>
                  {line.paper ? (
                    <>
                      <span className="compare__name">{line.paper.name}</span>
                      <span className="compare__sub">
                        {sub(line.paper.quantity, null, line.paper.amount)}
                      </span>
                    </>
                  ) : (
                    <span className="compare__missing">нет на бумаге</span>
                  )}
                </td>
                <td>
                  {line.draft ? (
                    <>
                      <span className="compare__name">{line.draft.goods}</span>
                      <span className="compare__sub">
                        {sub(line.draft.quantity, line.draft.unit, line.draft.net)}
                      </span>
                    </>
                  ) : (
                    <span className="compare__missing">нет в StoreHouse</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {(paperTotal || draftTotal) && (
            <tfoot>
              <tr>
                <td>
                  <span className="compare__sub">Итог</span>
                  <span className="compare__total">{paperTotal ?? '—'}</span>
                </td>
                <td>
                  <span className="compare__sub">Итог</span>
                  <span className="compare__total">{draftTotal ?? '—'}</span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

/** Сравнить построчно нечем — документ и бумага показываются по отдельности. */
function SeparateTables({ receipt, document }: { receipt: ReceiptDetail; document: string }) {
  const draftTotal = rub(receipt.draft?.total);
  const paperTotal = rub(receipt.paper?.total);
  const draftMoney = receipt.draft_lines.some((line) => line.net !== null);
  const paperMoney = receipt.paper?.lines.some((line) => line.amount !== null) ?? false;
  return (
    <>
      <div className="panel__block">
        <div className="block-title-row">
          <h3 className="block-title">{document}</h3>
          {draftTotal && <span className="block-total">{draftTotal}</span>}
        </div>
        {receipt.draft_lines.length === 0 ? (
          <p className="faint">
            {receipt.draft_lines_pending
              ? 'Строки этого документа ещё не прочитаны из StoreHouse. Агент запросил их — появятся здесь в течение получаса.'
              : 'В документе нет строк.'}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="lines">
              <thead>
                <tr>
                  <th>Товар</th>
                  <th className="num">Кол-во</th>
                  {draftMoney && <th className="num">Без НДС</th>}
                </tr>
              </thead>
              <tbody>
                {receipt.draft_lines.map((line, index) => (
                  <tr key={index}>
                    <td>{line.goods}</td>
                    <td className="num">
                      {qty(line.quantity)} {line.unit ?? ''}
                    </td>
                    {draftMoney && <td className="num">{rub(line.net) ?? '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {receipt.paper && (
        <div className="panel__block">
          <div className="block-title-row">
            <h3 className="block-title">Прочитано с фото</h3>
            {paperTotal && <span className="block-total">{paperTotal}</span>}
          </div>
          {receipt.paper.lines.length === 0 ? (
            <p className="faint">Строки с фото не прочитаны.</p>
          ) : (
            <div className="table-wrap">
              <table className="lines">
                <thead>
                  <tr>
                    <th>На бумаге</th>
                    <th className="num">Кол-во</th>
                    {paperMoney && <th className="num">Сумма</th>}
                  </tr>
                </thead>
                <tbody>
                  {receipt.paper.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.name}</td>
                      <td className="num">{qty(line.quantity)}</td>
                      {paperMoney && <td className="num">{rub(line.amount) ?? '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/* ── Ждёт черновика ──────────────────────────────────────────────────── */

function Waiting({ receipt }: { receipt: ReceiptDetail }) {
  return (
    <div className="panel__block">
      <h3 className="block-title">Черновика в StoreHouse пока нет</h3>
      <p className="muted">
        Агент ищет черновик этой накладной в StoreHouse при каждом чтении. Если поставщик не
        присылает электронный документ, черновик можно подготовить по фото — агент заполнит строки,
        а вы проверите их перед созданием.
      </p>
      {receipt.reasons.length > 0 && (
        <ul className="reasons">
          {receipt.reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ProposalStatus({ proposal }: { proposal: Proposal }) {
  const text: Record<string, string> = {
    creating: 'Черновик создаётся в StoreHouse — это займёт до десяти минут.',
    created: 'Черновик создан в StoreHouse. Он появится в очереди после следующего чтения.',
    failed: 'Создать черновик не получилось. Подготовьте его по фото заново.',
    cancelled: 'Черновик по фото отменён.',
  };
  return (
    <div className="panel__block">
      <h3 className="block-title">Черновик по фото</h3>
      <p className="muted">{text[proposal.status] ?? `Состояние: ${proposal.status}`}</p>
    </div>
  );
}

/* ── Кнопки ──────────────────────────────────────────────────────────── */

function Actions({ receipt }: { receipt: ReceiptDetail }) {
  const queryClient = useQueryClient();
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['receipts'] });
    void queryClient.invalidateQueries({ queryKey: ['today'] });
  };
  const failed = (error: ApiError) =>
    setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' });

  const mark = useMutation<Message, ApiError, Mark>({
    mutationFn: (action) => portalApi.mark(receipt.id, action),
    onSuccess: (data) => {
      setResult({ ok: true, text: data.message });
      refresh();
    },
    onError: failed,
  });

  const prepare = useMutation<Proposal, ApiError>({
    mutationFn: () => portalApi.prepare(receipt.id),
    onSuccess: (proposal) => {
      queryClient.setQueryData<ReceiptDetail>(receiptKey(receipt.id), (old) =>
        old ? { ...old, proposal } : old,
      );
      setResult({ ok: true, text: 'Черновик по фото готов — проверьте строки.' });
      refresh();
    },
    onError: failed,
  });

  const close = useMutation<Message, ApiError>({
    mutationFn: () => portalApi.closeReceipt(receipt.id),
    onSuccess: (data) => {
      setResult({ ok: true, text: data.message });
      refresh();
    },
    onError: failed,
  });

  const marks = receipt.actions.filter((action): action is Mark => action !== 'prepare_draft');
  const canPrepare = receipt.actions.includes('prepare_draft');
  // Убрать можно всё, что ещё не проведено и не закрыто; права проверит сервер.
  const canClose = ['waiting_draft', 'green', 'yellow', 'red'].includes(receipt.status);
  if (marks.length === 0 && !canPrepare && !canClose && !result) return null;
  const busy = mark.isPending || prepare.isPending || close.isPending;

  return (
    <div className="panel__actions">
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      {marks.length > 0 && (
        <div className="choices">
          {marks.map((action) => (
            <button
              key={action}
              type="button"
              className={`choice${action === 'posted_error' ? ' choice--danger' : ''}`}
              disabled={busy}
              onClick={() => mark.mutate(action)}
            >
              <span className="choice__title">{MARKS[action].title}</span>
              <span className="choice__hint">{MARKS[action].hint}</span>
            </button>
          ))}
        </div>
      )}
      <div className="row">
        {canPrepare && (
          <button
            type="button"
            className="button button--primary"
            disabled={busy}
            onClick={() => prepare.mutate()}
          >
            {prepare.isPending ? 'Готовлю…' : 'Подготовить черновик по фото'}
          </button>
        )}
        {canClose && (
          <button
            type="button"
            className="button button--quiet"
            title="Бумага лишняя — повтор, ошибка или не наша накладная. Фото сохранятся."
            disabled={busy}
            onClick={() => {
              if (window.confirm('Убрать эту накладную из всех списков? Фото сохранятся.')) {
                close.mutate();
              }
            }}
          >
            <Icon name="trash" size={16} /> Убрать накладную
          </button>
        )}
      </div>
    </div>
  );
}
